import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CheckCircle2, ArrowRight, Home, Video } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useLanguage, dfLocale } from "../i18n";

export default function ConfermaPrenotazione() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const { t, lang } = useLanguage();
  const loc = dfLocale[lang];

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  }, [id]);

  return (
    <>
      <Seo title={`${t("Appuntamento prenotato.")} — Alsera Studio`} description={t("Appuntamento prenotato.")} />
      <section className="pt-32 pb-24 sm:pt-40" data-testid="conferma-page">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          {error && (
            <div className="card-soft p-10 text-center" data-testid="conferma-error">
              <h1 className="text-2xl font-bold text-antracite">{t("Prenotazione non trovata")}</h1>
              <p className="mt-3 text-antracite/65">{t("Il link potrebbe non essere valido. Puoi prenotare di nuovo in pochi passi.")}</p>
              <Link to="/prenota" className="btn-primary mt-6">{t("Vai alla prenotazione")}</Link>
            </div>
          )}
          {!error && !booking && <p className="text-antracite/60">{t("Caricamento…")}</p>}
          {booking && (
            <Reveal>
              <div className="card-soft noise-overlay p-8 sm:p-12" data-testid="conferma-card">
                <span className="inline-flex rounded-full bg-acqua p-4 text-petrolio">
                  <CheckCircle2 className="h-9 w-9" />
                </span>
                <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-antracite sm:text-4xl">
                  {t("Appuntamento prenotato.")}
                </h1>
                <p className="mt-4 text-base leading-relaxed text-antracite/70">
                  {t("Grazie")} {booking.first_name}!{" "}
                  {booking.email_sent ? (
                    <>
                      {t("Ti abbiamo inviato un'email di conferma a")} <strong>{booking.email}</strong>{" "}
                      {t("con data, ora e dettagli dell'appuntamento. Se non la vedi, controlla la cartella spam.")}
                    </>
                  ) : (
                    <>
                      {t("Ti contatteremo all'indirizzo")} <strong>{booking.email}</strong>{" "}
                      {t("per confermare i dettagli dell'incontro.")}
                    </>
                  )}
                </p>
                <dl className="mt-8 space-y-3 rounded-2xl bg-acqua/50 p-6 text-sm">
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Codice prenotazione")}</dt><dd className="font-mono text-xs font-bold text-petrolio" data-testid="conferma-code">{booking.id.slice(0, 8).toUpperCase()}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Appuntamento")}</dt><dd className="font-bold text-antracite">{t(booking.type_name)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Data")}</dt><dd className="font-bold capitalize text-antracite" data-testid="conferma-date">{format(parseISO(booking.date), "EEEE d MMMM yyyy", { locale: loc })}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Orario")}</dt><dd className="font-bold text-antracite" data-testid="conferma-time">{booking.time} (Europe/Rome)</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Durata")}</dt><dd className="font-bold text-antracite">{booking.duration_minutes} {t("minuti")}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">Modalità</dt><dd className="flex items-center gap-1.5 font-bold text-antracite" data-testid="conferma-meet"><Video className="h-4 w-4 text-petrolio" /> Google Meet</dd></div>
                </dl>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to={`/prenotazione/${booking.id}`} className="btn-primary" data-testid="conferma-manage">
                    {t("Gestisci la prenotazione")}
                  </Link>
                  <Link to="/" className="btn-secondary" data-testid="conferma-home">
                    <Home className="h-4 w-4" /> {t("Torna alla home")}
                  </Link>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
